import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, Phone, Voicemail, Send, Search, Filter,
  User, Building, Clock, CheckCircle, AlertCircle, XCircle,
  RefreshCw, Plus, ExternalLink, ChevronRight, PhoneCall,
  PhoneIncoming, PhoneOutgoing, MessageCircle, Archive,
  Tag, Link as LinkIcon, StickyNote, MoreVertical
} from 'lucide-react';
import PhoneInput from '../components/PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommunicationsDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('calls');
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadNotes, setThreadNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  
  // New message
  const [newMessage, setNewMessage] = useState('');
  const [showNewMessageDialog, setShowNewMessageDialog] = useState(false);
  const [newMessageTo, setNewMessageTo] = useState('');
  const [newMessageBody, setNewMessageBody] = useState('');
  
  // Templates
  const [templates, setTemplates] = useState({});
  const [showTemplates, setShowTemplates] = useState(false);
  
  // Internal note
  const [newNote, setNewNote] = useState('');
  
  const messagesEndRef = useRef(null);

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      if (assignedFilter) params.append('assigned_to', assignedFilter);
      
      const response = await fetch(`${API_URL}/api/communications/threads?${params}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setThreads(data.threads || []);
      }
    } catch (error) {
      console.error('Error fetching threads:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, searchQuery, statusFilter, assignedFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/communications/stats`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [getAuthHeaders]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/communications/templates`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || {});
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, [getAuthHeaders]);

  const fetchThreadDetails = async (threadId) => {
    try {
      const response = await fetch(`${API_URL}/api/communications/threads/${threadId}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedThread(data.thread);
        setThreadMessages(data.messages || []);
        setThreadNotes(data.notes || []);
        
        // Scroll to bottom of messages
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching thread:', error);
    }
  };

  useEffect(() => {
    fetchThreads();
    fetchStats();
    fetchTemplates();
  }, [fetchThreads, fetchStats, fetchTemplates]);

  const sendReply = async () => {
    if (!newMessage.trim() || !selectedThread) return;
    
    try {
      const response = await fetch(`${API_URL}/api/communications/send`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          to_phone: selectedThread.from_phone,
          body: newMessage,
          linked_request_id: selectedThread.linked_request_ids?.[0]
        })
      });
      
      if (response.ok) {
        toast.success('Message sent');
        setNewMessage('');
        fetchThreadDetails(selectedThread.thread_id);
        fetchThreads();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Error sending message');
    }
  };

  const sendNewMessage = async () => {
    if (!newMessageTo.trim() || !newMessageBody.trim()) return;
    
    try {
      const response = await fetch(`${API_URL}/api/communications/send`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          to_phone: newMessageTo,
          body: newMessageBody
        })
      });
      
      if (response.ok) {
        toast.success('Message sent');
        setShowNewMessageDialog(false);
        setNewMessageTo('');
        setNewMessageBody('');
        fetchThreads();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Error sending message');
    }
  };

  const updateThreadStatus = async (status) => {
    if (!selectedThread) return;
    
    try {
      await fetch(`${API_URL}/api/communications/threads/${selectedThread.thread_id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });
      
      toast.success('Thread updated');
      fetchThreads();
      fetchThreadDetails(selectedThread.thread_id);
    } catch (error) {
      toast.error('Error updating thread');
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedThread) return;
    
    try {
      await fetch(`${API_URL}/api/communications/threads/${selectedThread.thread_id}/notes`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ note: newNote })
      });
      
      toast.success('Note added');
      setNewNote('');
      fetchThreadDetails(selectedThread.thread_id);
    } catch (error) {
      toast.error('Error adding note');
    }
  };

  const applyTemplate = (templateKey) => {
    const template = templates[templateKey];
    if (template) {
      setNewMessage(template.body);
      setShowTemplates(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const clean = phone.replace(/\D/g, '');
    if (clean.length === 11 && clean.startsWith('1')) {
      return `(${clean.slice(1, 4)}) ${clean.slice(4, 7)}-${clean.slice(7)}`;
    }
    if (clean.length === 10) {
      return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;
    }
    return phone;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={styles[status] || styles.open}>{status}</Badge>;
  };

  const getContactIcon = (type) => {
    if (type === 'interpreter') return <User className="h-4 w-4 text-orange-500" />;
    if (type === 'client') return <Building className="h-4 w-4 text-blue-500" />;
    return <MessageCircle className="h-4 w-4 text-gray-500" />;
  };

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="communications-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Communications</h1>
          <p className="text-sm text-gray-500">SMS Inbox, Calls & Voicemail</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Stats Badges */}
          {stats && (
            <div className="flex gap-2 mr-4">
              <Badge variant="outline" className="gap-1">
                <MessageSquare className="h-3 w-3" />
                {stats.threads?.open || 0} Open
              </Badge>
              <Badge variant="outline" className="gap-1 bg-red-50 text-red-700 border-red-200">
                {stats.threads?.unassigned || 0} Unassigned
              </Badge>
            </div>
          )}
          
          <Button variant="outline" onClick={() => { fetchThreads(); fetchStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          
          <Dialog open={showNewMessageDialog} onOpenChange={setShowNewMessageDialog}>
            <DialogTrigger asChild>
              <Button data-testid="new-message-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Message
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Send New Message</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium">To (Phone Number)</label>
                  <PhoneInput
                    value={newMessageTo}
                    onChange={(value) => setNewMessageTo(value)}
                    data-testid="sms-to-phone"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Message</label>
                  <Textarea
                    placeholder="Type your message..."
                    value={newMessageBody}
                    onChange={(e) => setNewMessageBody(e.target.value)}
                    rows={4}
                  />
                </div>
                <Button onClick={sendNewMessage} className="w-full">
                  <Send className="h-4 w-4 mr-2" />
                  Send Message
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        {/* Custom Tab Buttons for better visual distinction */}
        <div className="flex gap-3 mb-4 bg-gray-100 p-2 rounded-xl w-fit">
          <button
            type="button"
            onClick={() => setActiveTab('calls')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'calls'
                ? 'bg-green-600 text-white shadow-lg shadow-green-200'
                : 'bg-white text-gray-600 hover:bg-green-50 border border-gray-200 hover:border-green-200'
            }`}
          >
            <Phone className="h-5 w-5" />
            <span>Calls</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('sms')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'sms'
                ? 'bg-purple-600 text-white shadow-lg shadow-purple-200'
                : 'bg-white text-gray-600 hover:bg-purple-50 border border-gray-200 hover:border-purple-200'
            }`}
          >
            <MessageSquare className="h-5 w-5" />
            <span>SMS/Text</span>
            {stats?.threads?.open > 0 && (
              <Badge className={activeTab === 'sms' ? 'bg-purple-200 text-purple-800' : 'bg-purple-100 text-purple-700'}>{stats.threads.open}</Badge>
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('voicemail')}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'voicemail'
                ? 'bg-orange-500 text-white shadow-lg shadow-orange-200'
                : 'bg-white text-gray-600 hover:bg-orange-50 border border-gray-200 hover:border-orange-200'
            }`}
          >
            <Voicemail className="h-5 w-5" />
            <span>Voicemail</span>
          </button>
        </div>

        <TabsContent value="sms" className="flex-1 flex gap-4 mt-0">
          {/* Thread List */}
          <Card className="w-80 flex flex-col">
            <CardHeader className="pb-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search conversations..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex gap-2 mt-2">
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-0">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : threads.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p>No conversations</p>
                </div>
              ) : (
                <div className="divide-y">
                  {threads.map((thread) => (
                    <div
                      key={thread.thread_id}
                      className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedThread?.thread_id === thread.thread_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                      onClick={() => fetchThreadDetails(thread.thread_id)}
                      data-testid={`thread-${thread.thread_id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="mt-1">{getContactIcon(thread.contact_type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm truncate">
                              {thread.contact_name || formatPhone(thread.from_phone)}
                            </span>
                            <span className="text-xs text-gray-500">{formatTime(thread.last_message_at)}</span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">{formatPhone(thread.from_phone)}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {getStatusBadge(thread.status)}
                            {thread.unread_count > 0 && (
                              <Badge className="bg-red-500 text-white text-xs">{thread.unread_count}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Message Thread */}
          <Card className="flex-1 flex flex-col">
            {selectedThread ? (
              <>
                <CardHeader className="border-b pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getContactIcon(selectedThread.contact_type)}
                      <div>
                        <CardTitle className="text-lg">
                          {selectedThread.contact_name || formatPhone(selectedThread.from_phone)}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2">
                          {formatPhone(selectedThread.from_phone)}
                          {selectedThread.contact_type && (
                            <Badge variant="outline" className="text-xs">
                              {selectedThread.contact_type}
                            </Badge>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select 
                        value={selectedThread.status} 
                        onValueChange={updateThreadStatus}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      {selectedThread.linked_request_ids?.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/work-orders/${selectedThread.linked_request_ids[0]}`)}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View WO
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                  {threadMessages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg p-3 ${
                          msg.direction === 'outbound'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                        <div className={`text-xs mt-1 ${
                          msg.direction === 'outbound' ? 'text-blue-200' : 'text-gray-500'
                        }`}>
                          {formatTime(msg.created_at)}
                          {msg.is_auto_response && ' • Auto'}
                          {msg.sent_by_name && ` • ${msg.sent_by_name}`}
                          {msg.status === 'failed' && (
                            <Badge variant="destructive" className="ml-2 text-xs">Failed</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </CardContent>

                {/* Message Input */}
                <div className="border-t p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowTemplates(!showTemplates)}
                    >
                      <Tag className="h-4 w-4 mr-1" />
                      Templates
                    </Button>
                  </div>
                  
                  {showTemplates && (
                    <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-2">Quick Templates:</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(templates).slice(0, 6).map(([key, tpl]) => (
                          <Button
                            key={key}
                            variant="outline"
                            size="sm"
                            onClick={() => applyTemplate(key)}
                            className="text-xs"
                          >
                            {tpl.name}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Type your reply..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      rows={2}
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendReply();
                        }
                      }}
                    />
                    <Button onClick={sendReply} disabled={!newMessage.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">Select a conversation</p>
                  <p className="text-sm">Choose a thread from the list to view messages</p>
                </div>
              </div>
            )}
          </Card>

          {/* Context Panel */}
          {selectedThread && (
            <Card className="w-72">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Contact Info */}
                <div>
                  <h4 className="text-xs font-medium text-gray-500 mb-2">CONTACT</h4>
                  <div className="space-y-1 text-sm">
                    <p className="flex items-center gap-2">
                      {getContactIcon(selectedThread.contact_type)}
                      {selectedThread.contact_name || 'Unknown'}
                    </p>
                    <p className="text-gray-500">{formatPhone(selectedThread.from_phone)}</p>
                    {selectedThread.contact_type && (
                      <Badge variant="outline">{selectedThread.contact_type}</Badge>
                    )}
                  </div>
                </div>

                {/* Linked Requests */}
                {selectedThread.linked_request_ids?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 mb-2">LINKED REQUESTS</h4>
                    <div className="space-y-1">
                      {selectedThread.linked_request_ids.map((id) => (
                        <Button
                          key={id}
                          variant="outline"
                          size="sm"
                          className="w-full justify-start text-xs"
                          onClick={() => navigate(`/work-orders/${id}`)}
                        >
                          <LinkIcon className="h-3 w-3 mr-2" />
                          {id}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {selectedThread.tags?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 mb-2">TAGS</h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedThread.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Internal Notes */}
                <div>
                  <h4 className="text-xs font-medium text-gray-500 mb-2">
                    <StickyNote className="h-3 w-3 inline mr-1" />
                    INTERNAL NOTES
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {threadNotes.map((note) => (
                      <div key={note.note_id} className="bg-yellow-50 p-2 rounded text-xs">
                        <p>{note.note}</p>
                        <p className="text-gray-500 mt-1">
                          {note.created_by_name} • {formatTime(note.created_at)}
                        </p>
                      </div>
                    ))}
                    {threadNotes.length === 0 && (
                      <p className="text-xs text-gray-400">No notes yet</p>
                    )}
                  </div>
                  <div className="mt-2 flex gap-1">
                    <Input
                      placeholder="Add note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="text-xs"
                    />
                    <Button size="sm" variant="outline" onClick={addNote}>
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="calls" className="flex-1">
          <CallsTab 
            API_URL={API_URL} 
            getAuthHeaders={getAuthHeaders} 
            navigate={navigate}
            formatPhone={formatPhone}
            formatTime={formatTime}
          />
        </TabsContent>

        <TabsContent value="voicemail" className="flex-1">
          <VoicemailTab 
            API_URL={API_URL} 
            getAuthHeaders={getAuthHeaders} 
            navigate={navigate}
            formatPhone={formatPhone}
            formatTime={formatTime}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ==================== CALLS TAB ====================
const CallsTab = ({ API_URL, getAuthHeaders, navigate, formatPhone, formatTime }) => {
  const [calls, setCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showInitiateCall, setShowInitiateCall] = useState(false);
  const [callToPhone, setCallToPhone] = useState('');

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/communications/calls`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCalls(data.calls || []);
      }
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  const initiateCall = async () => {
    if (!callToPhone.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/communications/voice/initiate`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ to_phone: callToPhone })
      });
      if (response.ok) {
        toast.success('Call initiated');
        setShowInitiateCall(false);
        setCallToPhone('');
        fetchCalls();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to initiate call');
      }
    } catch (error) {
      toast.error('Error initiating call');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCallStatusConfig = (status) => {
    const configs = {
      ringing: { color: 'bg-amber-500', text: 'Ringing', icon: '📞' },
      in_progress: { color: 'bg-green-500', text: 'In Progress', icon: '🔊' },
      completed: { color: 'bg-blue-500', text: 'Completed', icon: '✓' },
      no_answer: { color: 'bg-orange-500', text: 'No Answer', icon: '📵' },
      busy: { color: 'bg-red-500', text: 'Busy', icon: '🔴' },
      failed: { color: 'bg-red-600', text: 'Failed', icon: '✗' },
      canceled: { color: 'bg-gray-500', text: 'Canceled', icon: '⊘' }
    };
    return configs[status] || { color: 'bg-gray-400', text: status, icon: '?' };
  };

  return (
    <div className="flex gap-6 h-full">
      {/* Call List - Modern Design */}
      <div className="w-[420px] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Call History</h2>
            <p className="text-sm text-gray-500">{calls.length} total calls</p>
          </div>
          <Dialog open={showInitiateCall} onOpenChange={setShowInitiateCall}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700">
                <PhoneCall className="h-4 w-4 mr-2" />
                New Call
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-green-500" />
                  Make a Call
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Phone Number</label>
                  <PhoneInput
                    value={callToPhone}
                    onChange={(value) => setCallToPhone(value)}
                    className="mt-1"
                    data-testid="call-to-phone"
                  />
                </div>
                <Button onClick={initiateCall} className="w-full bg-green-600 hover:bg-green-700">
                  <Phone className="h-4 w-4 mr-2" />
                  Start Call
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : calls.length === 0 ? (
            <div className="text-center py-16 bg-gradient-to-b from-gray-50 to-white rounded-xl border-2 border-dashed border-gray-200">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <Phone className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">No calls yet</p>
              <p className="text-sm text-gray-400 mt-1">Calls will appear here when you receive or make them</p>
            </div>
          ) : (
            calls.map((call) => {
              const statusConfig = getCallStatusConfig(call.status);
              const isSelected = selectedCall?.call_id === call.call_id;
              
              return (
                <div
                  key={call.call_id}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-100' 
                      : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-md'
                  }`}
                  onClick={() => setSelectedCall(call)}
                >
                  <div className="flex items-center gap-4">
                    {/* Direction Icon */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      call.direction === 'inbound' 
                        ? 'bg-gradient-to-br from-green-400 to-green-600' 
                        : 'bg-gradient-to-br from-blue-400 to-blue-600'
                    }`}>
                      {call.direction === 'inbound' ? (
                        <PhoneIncoming className="h-5 w-5 text-white" />
                      ) : (
                        <PhoneOutgoing className="h-5 w-5 text-white" />
                      )}
                    </div>
                    
                    {/* Call Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 truncate">
                          {call.contact_name || formatPhone(call.direction === 'inbound' ? call.from_phone : call.to_phone)}
                        </span>
                        {call.contact_type && (
                          <Badge variant="outline" className="text-xs">
                            {call.contact_type}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                        <span>{call.direction === 'inbound' ? 'Incoming' : 'Outgoing'}</span>
                        <span>•</span>
                        <span>{formatTime(call.started_at)}</span>
                      </div>
                    </div>
                    
                    {/* Status & Duration */}
                    <div className="text-right">
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white ${statusConfig.color}`}>
                        <span>{statusConfig.icon}</span>
                        <span>{statusConfig.text}</span>
                      </div>
                      <p className="text-sm font-mono text-gray-600 mt-1">
                        {formatDuration(call.duration_seconds)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Recording indicator */}
                  {call.recording_url && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-500">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                      Recording available
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Call Details - Modern Design */}
      <Card className="flex-1 overflow-hidden">
        {selectedCall ? (
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  selectedCall.direction === 'inbound' 
                    ? 'bg-gradient-to-br from-green-400 to-green-600' 
                    : 'bg-gradient-to-br from-blue-400 to-blue-600'
                }`}>
                  {selectedCall.direction === 'inbound' ? (
                    <PhoneIncoming className="h-7 w-7 text-white" />
                  ) : (
                    <PhoneOutgoing className="h-7 w-7 text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold">
                    {selectedCall.contact_name || formatPhone(selectedCall.direction === 'inbound' ? selectedCall.from_phone : selectedCall.to_phone)}
                  </h2>
                  <p className="text-slate-300 flex items-center gap-2 mt-1">
                    {selectedCall.direction === 'inbound' ? 'Incoming call' : 'Outgoing call'}
                    <span>•</span>
                    {formatTime(selectedCall.started_at)}
                  </p>
                </div>
                <div className={`px-4 py-2 rounded-full text-sm font-medium ${getCallStatusConfig(selectedCall.status).color}`}>
                  {getCallStatusConfig(selectedCall.status).text}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-6 overflow-y-auto space-y-6">
              {/* Call Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Duration</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1 font-mono">
                    {formatDuration(selectedCall.duration_seconds)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">From</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {formatPhone(selectedCall.from_phone)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">To</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {formatPhone(selectedCall.to_phone)}
                  </p>
                </div>
              </div>

              {/* Recording */}
              {selectedCall.recording_url && (
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-5 border border-red-100">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
                    <span className="font-semibold text-gray-900">Call Recording</span>
                  </div>
                  <audio controls className="w-full">
                    <source src={selectedCall.recording_url} type="audio/mpeg" />
                  </audio>
                </div>
              )}

              {/* Linked Work Order */}
              {selectedCall.linked_request_id && (
                <Button
                  variant="outline"
                  className="w-full justify-between py-6 border-2"
                  onClick={() => navigate(`/work-orders/${selectedCall.linked_request_id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <ExternalLink className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">Linked Work Order</p>
                      <p className="text-sm text-gray-500">{selectedCall.linked_request_id}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </Button>
              )}

              {/* Notes */}
              {selectedCall.notes?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <StickyNote className="h-4 w-4" />
                    Notes
                  </h3>
                  <div className="space-y-2">
                    {selectedCall.notes.map((note, i) => (
                      <div key={i} className="bg-yellow-50 border border-yellow-100 rounded-lg p-3">
                        <p className="text-gray-800">{note.note}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {note.created_by_name} • {formatTime(note.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <Phone className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700">Select a Call</h3>
              <p className="text-gray-500 mt-2">Choose a call from the list to view details</p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

// ==================== VOICEMAIL TAB ====================
const VoicemailTab = ({ API_URL, getAuthHeaders, navigate, formatPhone, formatTime }) => {
  const [voicemails, setVoicemails] = useState([]);
  const [selectedVoicemail, setSelectedVoicemail] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchVoicemails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/communications/voicemails`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVoicemails(data.voicemails || []);
      }
    } catch (error) {
      console.error('Error fetching voicemails:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    fetchVoicemails();
  }, [fetchVoicemails]);

  const updateVoicemailStatus = async (status) => {
    if (!selectedVoicemail) return;
    try {
      await fetch(`${API_URL}/api/communications/voicemails/${selectedVoicemail.voicemail_id}/status?status=${status}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      toast.success('Status updated');
      fetchVoicemails();
      setSelectedVoicemail({ ...selectedVoicemail, status });
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const getVoicemailStatusBadge = (status) => {
    const styles = {
      new: 'bg-red-100 text-red-800',
      listened: 'bg-yellow-100 text-yellow-800',
      callback_scheduled: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  return (
    <div className="flex gap-4 h-full">
      {/* Voicemail List */}
      <Card className="w-96 flex flex-col">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Voicemails</CardTitle>
          <CardDescription>
            {voicemails.filter(v => v.status === 'new').length} new
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : voicemails.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Voicemail className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p>No voicemails</p>
            </div>
          ) : (
            <div className="divide-y">
              {voicemails.map((vm) => (
                <div
                  key={vm.voicemail_id}
                  className={`p-3 cursor-pointer hover:bg-gray-50 ${selectedVoicemail?.voicemail_id === vm.voicemail_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''} ${vm.status === 'new' ? 'bg-red-50' : ''}`}
                  onClick={() => setSelectedVoicemail(vm)}
                >
                  <div className="flex items-center gap-2">
                    <Voicemail className={`h-4 w-4 ${vm.status === 'new' ? 'text-red-500' : 'text-gray-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {vm.contact_name || formatPhone(vm.from_phone)}
                      </p>
                      <p className="text-xs text-gray-500">{formatTime(vm.created_at)}</p>
                    </div>
                    {getVoicemailStatusBadge(vm.status)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{vm.duration_seconds}s</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Voicemail Details */}
      <Card className="flex-1">
        {selectedVoicemail ? (
          <>
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Voicemail className="h-6 w-6 text-blue-500" />
                  <div>
                    <CardTitle>{selectedVoicemail.contact_name || formatPhone(selectedVoicemail.from_phone)}</CardTitle>
                    <CardDescription>
                      {formatTime(selectedVoicemail.created_at)} • {selectedVoicemail.duration_seconds}s
                    </CardDescription>
                  </div>
                </div>
                <Select value={selectedVoicemail.status} onValueChange={updateVoicemailStatus}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="listened">Listened</SelectItem>
                    <SelectItem value="callback_scheduled">Callback Scheduled</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">From</p>
                  <p className="font-medium">{formatPhone(selectedVoicemail.from_phone)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">To</p>
                  <p className="font-medium">{formatPhone(selectedVoicemail.to_phone)}</p>
                </div>
              </div>

              {selectedVoicemail.recording_url && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium mb-2">Recording</p>
                  <audio controls className="w-full">
                    <source src={selectedVoicemail.recording_url} type="audio/mpeg" />
                  </audio>
                </div>
              )}

              {selectedVoicemail.transcript_text ? (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Transcript
                  </p>
                  <p className="text-sm text-gray-700">{selectedVoicemail.transcript_text}</p>
                </div>
              ) : (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm text-yellow-700 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Transcription {selectedVoicemail.transcription_status === 'pending' ? 'in progress...' : selectedVoicemail.transcription_status}
                  </p>
                </div>
              )}

              {selectedVoicemail.linked_request_id && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/work-orders/${selectedVoicemail.linked_request_id}`)}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Linked Work Order
                </Button>
              )}

              <div className="text-xs text-gray-500 pt-4 border-t">
                <p>Retention expires: {selectedVoicemail.retention_expires_at?.slice(0, 10)}</p>
                {selectedVoicemail.email_sent && (
                  <p className="text-green-600">Email notification sent ✓</p>
                )}
              </div>
            </CardContent>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500 h-full">
            <div className="text-center">
              <Voicemail className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">Select a voicemail</p>
              <p className="text-sm">Choose a voicemail from the list to view details and transcript</p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CommunicationsDashboard;
